import {
  DispositionType,
  Shipment,
  ShippingFile,
  ShippingFileType,
  ShippingRecord,
} from "$types";
import { getDeliveryLocationLabelAlt } from "./PreparationOrder";

export const getShipmentDeliveryLocationEntityId = (
  shipment: Shipment
): string | undefined =>
  shipment?.toAddress?.entityId ||
  (shipment?.op?.shipType === DispositionType.ON_DOCKYARD
    ? shipment?.op?.chantier?._id
    : shipment?.op?.shipEntrepotName);

export const getRecordProofOfDeliveryFiles = (
  shippingRecord: ShippingRecord
): ShippingFile[] =>
  (shippingRecord?.files || []).filter(
    (file) =>
      file.type === ShippingFileType.PROOF_OF_DELIVERY &&
      file.deliveryLocationEntityId
  );

export const getUniqueDeliveryLocationShipments = (
  shipments: Shipment[] = []
): Shipment[] => {
  const seen = new Set<string>();
  return shipments.filter((shipment) => {
    const deliveryLocationEntityId =
      getShipmentDeliveryLocationEntityId(shipment);
    if (!deliveryLocationEntityId || seen.has(deliveryLocationEntityId)) {
      return false;
    }
    seen.add(deliveryLocationEntityId);
    return true;
  });
};

export const getDeliveryLocationsWithoutProof = (
  shippingRecord: ShippingRecord
) => {
  const existingDeliveryLocationEntityIds = getRecordProofOfDeliveryFiles(
    shippingRecord
  ).map((file) => file.deliveryLocationEntityId);

  return getUniqueDeliveryLocationShipments(shippingRecord?.shipments)
    .filter((shipment) => {
      const deliveryLocationEntityId =
        getShipmentDeliveryLocationEntityId(shipment);
      return (
        deliveryLocationEntityId &&
        !existingDeliveryLocationEntityIds.includes(deliveryLocationEntityId)
      );
    })
    .map((shipment) => ({
      label: getDeliveryLocationLabelAlt(shipment?.op),
      value: getShipmentDeliveryLocationEntityId(shipment),
    }));
};

export const canAddProofOfDelivery = (
  shippingRecord: ShippingRecord
): boolean => {
  if (!shippingRecord?.shipments?.length) {
    return false;
  }
  const uniqueLocations = getUniqueDeliveryLocationShipments(
    shippingRecord.shipments
  ).length;
  const existingProofs = getRecordProofOfDeliveryFiles(shippingRecord).length;
  return existingProofs < uniqueLocations;
};

export const getProofFileForShipment = (
  shipment: Shipment,
  shippingRecord: ShippingRecord
) => {
  const deliveryLocationEntityId =
    getShipmentDeliveryLocationEntityId(shipment);
  const recordProof = getRecordProofOfDeliveryFiles(shippingRecord).find(
    (file) => file.deliveryLocationEntityId === deliveryLocationEntityId
  );
  return recordProof || shipment.proofOfDelivery;
};

export const getDeliveryLocationShipmentsWithProof = (
  shippingRecord: ShippingRecord
): Shipment[] =>
  getUniqueDeliveryLocationShipments(shippingRecord?.shipments).filter(
    (shipment) => !!getProofFileForShipment(shipment, shippingRecord)
  );
